import type {
  AirHandlerProductType,
  CondenserProductType,
  ProductWarrantyType,
  SelectedSystemType
} from '@alpinehomeair/common';
import type { SizingBandsSettingType } from 'data/types';
import { nanoid } from 'nanoid';
import {
  CompatibilityDesignPair,
  CompatibilityPairRef
} from 'utils/compatibility-bundle-types';
import { stampCompatibilityBundleFields } from 'utils/compatibility-bundle-fields';
import { condenserProductDescription } from 'utils/condenser-product-description';
import type { ConfidenceScoreResultType } from 'utils/confidence-score';
import {
  combineDuctedCoverage,
  combineDuctedOperatingCost
} from 'utils/ducted-bundle-metrics';
import {
  stampZoneDuctedCoverage,
  type DuctedCoverageType
} from 'utils/ducted-coverage';
import type {
  DuctedBinCoverage,
  DuctedOperatingCost
} from 'utils/ducted-system-metrics';
import {
  EquipmentPlaceholder,
  equipmentPlaceholderDescription,
  equipmentPlaceholderName,
  equipmentPlaceholderNoteText,
  KEPT_EQUIPMENT_PRODUCT_ID,
  PlaceholderNoteFields
} from 'utils/equipment-placeholder';
import { isHeatPumpSystemTypeLabel } from 'utils/system-type-label';

// Frontend mirror of `FullProductData` from system-selector-api. We deliberately
// duplicate the shape rather than importing across the package boundary —
// system-selector-gui already treats the API as a remote service, and this
// keeps the utility self-contained for testing.
export type FullProductData = {
  productId: number;
  manufacturer: string;
  modelNumber: string;
  name: string;
  description: string | null;
  price: number | null;
  isOnSale: boolean;
  weight: number | null;
  productUrl: string | null;
  photo: string | null;
  fulfillableStock: number;
  series: string | null;
  seriesDescription: string | null;
  seriesCode: string | null;
  height: string | null;
  width: string | null;
  depth: string | null;
  tonnage: string | null;
  stagesOfCool: string | null;
  stagesOfHeat: string | null;
  compressorType: string | null;
  soundRating: string | null;
  refrigerant: string | null;
  mobileHomeCompat: string | null;
  afue: string | null;
  blowerType: string | null;
  maxHeatOutput: string | null;
  cabinetSize: string | null;
  orientation: string | null;
  productType: string | null;
  voltage: string | null;
  amperage: string | null;
  warranties: ProductWarrantyType[];
  placeholderNote?: string | null;
};

export type CombinationPerformance = {
  seer2: number | null;
  eer2: number | null;
  hspf2: number | null;
  coolingCapacity: number | null;
  heatingCapacity: number | null;
  afue: number | null;
};

export type CompatibilityAccessoryInput = {
  productId: number;
  name: string;
  description: string | null;
  price: number;
  accessoryTypeCode: string;
  productUrl: string;
  photo: string;
  fulfillableStock: number;
};

export type CompatibilityPairParams = {
  configurationUniqueId: string;
  configurationLabel: string;
  systemTypeLabel: string;
  combinationPerformance: CombinationPerformance;
  outdoorProduct: FullProductData | null;
  indoorProduct: FullProductData | null;
  blowerProduct: FullProductData | null;
  // Subset of {outdoor, indoor, blower} productIds that are being replaced.
  // Drives price/inStock/onSale/manufacturers aggregates so kept equipment
  // never contributes to those.
  replacingProductIds: Set<number>;
  // Human-readable label for kept outdoor/indoor equipment, extracted from
  // the combination's systemTypeLabel (e.g. "Heat Pump", "Evaporator Coil").
  // Used to build descriptive placeholder names when the slot is null.
  keptOutdoorLabel?: string;
  keptIndoorLabel?: string;
  // Kept blower/furnace slot (AHA-6796): set ONLY when the configuration has
  // a non-replacing blower position and the combination's blower slot is
  // null — a 2-part system type with no blower position stays a null slot
  // (no placeholder row). The heating capacity comes from the kept blower
  // EE component's spec field (BTU/hr) when the user recorded one.
  keptBlowerLabel?: string;
  keptBlowerHeatingCapacity?: number | null;
  requiredOutdoorLabel?: string;
  requiredIndoorLabel?: string;
  requiredBlowerLabel?: string;
  // A packaged system type, named ("Packaged Gas"): the coil and blower are
  // built into the unit the outdoor slot sells, so the open indoor slot is
  // that unit's own section — carrying the combination's capacities — and
  // the blower slot stays empty. Never kept, never required.
  packagedUnitLabel?: string;
  // The combination came from the kept-constrained result set (AHA-6681
  // sub-build 4): adds KEPT_PAIRING_TAG so the marker survives into the
  // comparison chips and the quoted/emailed artifact descriptions.
  keptPairing?: boolean;
  // Kept-aware "Customer's <equipment>" display label (AHA-6681 display
  // titles): default `name` for the manufactured system. Everything reading
  // `name` (comparison headers, quote column, kit description base) inherits
  // it; user renames still win because it is only the default.
  displayName?: string;
  // Server-computed design-load coverage (AHA-6796, Phase 6 response).
  // Carried verbatim onto the manufactured system (null percents = "—") AND
  // folded into heatingScore/coolingScore as `?? 0` — the scores are
  // non-nullable z.number() so they can't carry "not computable", but
  // populating them lets alpine-client's existing v2Data reads self-fix.
  ductedCoverage?: DuctedCoverageType | null;
  // Operating cost and bin coverage carried off the compatible-combination
  // row the system was manufactured from. Both are computed once, at
  // combination-fetch time, against the project's climate — recomputing them
  // here would risk a different answer than the card the user picked. The
  // total also lands on totalAnnualCost so every existing cost surface (the
  // comparison modal included) reads it without knowing about ducted.
  operatingCost?: DuctedOperatingCost | null;
  binCoverage?: DuctedBinCoverage | null;
  // AHA-6796 quote-time snapshot of the admin sizingBands setting, resolved
  // per system type by resolveZoneSizingThresholds. Attached to the built
  // ducted zone so alpine-client's match labels can consume the bands the
  // quote was filtered with. null / absent (older API deploy, invalid
  // record) attaches nothing → legacy hardcoded labels downstream.
  sizingBands?: SizingBandsSettingType | null;
  // The existing (ducted) system the combination was browsed for. Stamped on
  // the manufactured system so the quote-time layout builder can read that
  // system's placed equipment positions; absent on systems persisted before
  // the stamp existed (the builder then falls back to a heuristic).
  existingSystemUniqueId?: string;
  compatibilityCombinationId?: number;
  // Required accessories persisted by Phase 3 and returned by Phase 6.
  // Mapped 1:1 into quotableAccessories (quantity 1) so they render in the
  // quote preview's Show All Items section.
  accessories?: CompatibilityAccessoryInput[];
  designPair?: CompatibilityDesignPair | null;
  cardId?: string | null;
};

export type BuildSelectedSystemFromCompatibilityParams =
  CompatibilityPairParams & {
    quotableSystemUniqueId: string;
    // Server-computed project-level confidence score (AHA-6796 memo §10,
    // Phase 6 response sibling of quotableSystems). One score per project,
    // stamped verbatim on each built system so it rides the quoteData POST
    // into the persisted quote payload. Null/absent (non-ducted project,
    // older API deploy) attaches nothing.
    confidence?: ConfidenceScoreResultType | null;
  };

// Tag attached to manufactured systems so downstream gating (e.g. accessory
// flows, scoring) can distinguish compatibility-sourced systems from
// scored/custom systems built through the normal flow.
export const COMPATIBILITY_MATCH_TAG = 'Compatibility Match';

// Tag attached to systems manufactured from the kept-constrained result set
// (AHA-6681 sub-build 4). Descriptive tags render as chips wherever
// SystemTitleTags does (comparison surfaces), and buildQuoteKitDescription
// carries the marker into the quoted/emailed artifacts.
export const KEPT_PAIRING_TAG = "Pairs with Customer's Equipment";

// The tag's original wording (AHA-6681 display titles renamed it). Persisted
// systems — autosaved systemsToCompare / selected systems — may still carry
// this value, so tag consumers accept BOTH and normalize to the new label
// for display. Never written to new systems.
export const LEGACY_KEPT_PAIRING_TAG = 'Pairs with Kept Equipment';

// Kit description for the quote-email artifacts (send-quote /
// email-single-quote modals). Compatibility-sourced systems are described
// by their system-type name; the kept-pairing marker rides along so the
// distinction survives into the quoted artifact (approved spec, sub-build
// 4). Old persisted systems carry the legacy tag value; new quotes built
// from them still get the NEW marker wording (ruling 7 — already-sent
// quotes are untouched).
export function buildQuoteKitDescription(system: {
  name: string;
  numberOfZones: number;
  tags: string[];
}): string {
  const base = system.tags.includes(COMPATIBILITY_MATCH_TAG)
    ? system.name
    : `${system.numberOfZones} Zone Mini Split System`;
  return system.tags.includes(KEPT_PAIRING_TAG) ||
    system.tags.includes(LEGACY_KEPT_PAIRING_TAG)
    ? `${base} (Pairs with Customer's Equipment)`
    : base;
}

// tblProductAttributes stores everything as strings. Numeric attributes can be
// bare ("27"), decimal ("14.3"), or ranges ("14.3 - 16"). We only call this for
// component-level numeric attributes (width/height/depth/weight/etc.); for
// system-level numerics (SEER, cooling capacity) the caller uses the
// combination row directly.
const parseAttributeNumber = (value: string | null | undefined): number => {
  if (value == null) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseAttributeInt = (value: string | null | undefined): number => {
  if (value == null) return 0;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function readExistingSystemUniqueId(
  system: object | null | undefined
): string | null {
  if (!system) return null;
  const value = (system as { existingSystemUniqueId?: unknown })
    .existingSystemUniqueId;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

const buildCondenserProduct = (
  outdoor: FullProductData,
  performance: CombinationPerformance
): CondenserProductType & PlaceholderNoteFields => ({
  productId: outdoor.productId,
  name: outdoor.name,
  placeholderNote: outdoor.placeholderNote,
  model: outdoor.modelNumber,
  description: condenserProductDescription(
    outdoor.description,
    outdoor.productType
  ),
  seer: performance.seer2 ?? 0,
  coolingBTU: performance.coolingCapacity ?? 0,
  diy: false,
  hyperHeat: false,
  price: outdoor.price ?? 0,
  temperatures: [],
  accessories: [],
  photo: outdoor.photo ?? '',
  productUrl: outdoor.productUrl ?? '',
  series: outdoor.series ?? '',
  seriesDescription: outdoor.seriesDescription ?? '',
  seriesCode: outdoor.seriesCode,
  warranties: outdoor.warranties,
  requirements: [],
  dimensions: {
    width: parseAttributeNumber(outdoor.width),
    height: parseAttributeNumber(outdoor.height),
    depth: parseAttributeNumber(outdoor.depth),
    footprintMountingHoles: ''
  },
  weight: outdoor.weight ?? 0,
  voltage: outdoor.voltage ?? '',
  features: [],
  isOnSale: outdoor.isOnSale,
  amperage: parseAttributeNumber(outdoor.amperage),
  outdoorVisibility: 'Most Visible'
});

const buildAirHandlerProductFromIndoor = (
  indoor: FullProductData,
  performance: CombinationPerformance,
  blowerHasHeat: boolean,
  // The packaged-unit section stands in for the indoor slot with the
  // sentinel productId, but it IS the sold unit's coil and blower, so it
  // shows the combination's figures where a kept placeholder shows none.
  packagedSection = false
): AirHandlerProductType & PlaceholderNoteFields => {
  const showsCombinationCapacity =
    packagedSection || indoor.productId !== KEPT_EQUIPMENT_PRODUCT_ID;
  return {
    productId: indoor.productId,
    name: indoor.name,
    placeholderNote: indoor.placeholderNote,
    model: indoor.modelNumber,
    description: indoor.description ?? '',
    // productType attribute holds values like "Evaporator Coil" /
    // "Multi-Positional Air Handler"; fall back to a coil-flavored default
    // when absent.
    type: indoor.productType ?? 'evap_coil',
    size: indoor.cabinetSize ?? '',
    sizeCode: indoor.cabinetSize ?? '',
    // Coils/air handlers have no per-product capacity attribute; the
    // combination row is the authoritative capacity source (same numbers
    // the ducted detail panel displays). Kept placeholders stay at 0 — kept
    // components show EE spec capacity or "—", never combination numbers.
    // Heating belongs to the furnace/blower row when that row has its own
    // heat output (maxHeatOutput attribute / kept EE spec); otherwise the
    // combination heating (heat-pump systems, attribute-less blowers like
    // Modular Blowers) rides the indoor row. Kept indoor placeholders
    // always stay 0.
    coolingBTU: showsCombinationCapacity ? performance.coolingCapacity ?? 0 : 0,
    heatingBTU:
      showsCombinationCapacity && !blowerHasHeat
        ? performance.heatingCapacity ?? 0
        : 0,
    price: indoor.price ?? 0,
    productUrl: indoor.productUrl ?? '',
    photo: indoor.photo ?? '',
    wifiEnabled: false,
    accessories: [],
    warranties: indoor.warranties,
    requirements: [],
    dimensions: {
      width: parseAttributeNumber(indoor.width),
      height: parseAttributeNumber(indoor.height),
      depth: parseAttributeNumber(indoor.depth)
    },
    weight: indoor.weight ?? 0,
    aestheticsRank: 0,
    installComplexityRank: 0,
    isOnSale: indoor.isOnSale,
    seriesCode: indoor.seriesCode
  };
};

const buildAirHandlerProductFromBlower = (
  blower: FullProductData
): AirHandlerProductType & PlaceholderNoteFields => ({
  productId: blower.productId,
  name: blower.name,
  placeholderNote: blower.placeholderNote,
  model: blower.modelNumber,
  description: blower.description ?? '',
  type: blower.productType ?? 'blower',
  size: blower.cabinetSize ?? '',
  sizeCode: blower.cabinetSize ?? '',
  coolingBTU: 0,
  // maxHeatOutput is BTU/hr expressed as an integer string ("80000"); furnaces
  // and modular blowers populate this attribute, evap coils do not.
  heatingBTU: parseAttributeInt(blower.maxHeatOutput),
  price: blower.price ?? 0,
  productUrl: blower.productUrl ?? '',
  photo: blower.photo ?? '',
  wifiEnabled: false,
  accessories: [],
  warranties: blower.warranties,
  requirements: [],
  dimensions: {
    width: parseAttributeNumber(blower.width),
    height: parseAttributeNumber(blower.height),
    depth: parseAttributeNumber(blower.depth)
  },
  weight: blower.weight ?? 0,
  aestheticsRank: 0,
  installComplexityRank: 0,
  isOnSale: blower.isOnSale,
  seriesCode: blower.seriesCode
});

type MissingSlot = 'outdoor' | 'indoor' | 'blower';

// Lightweight placeholder for a slot the combination leaves open — equipment
// the homeowner is keeping, or a component they still have to source.
// Contains enough structure to satisfy CondenserProductType /
// AirHandlerProductType without contributing to price, stock, or
// manufacturer aggregates.
const KEPT_SLOT_DEFAULT_LABELS: Record<MissingSlot, string> = {
  outdoor: 'Outdoor Unit',
  indoor: 'Indoor Unit',
  // Matches the pipeline's slotLabels wording for the blower position.
  blower: 'Blower/Furnace'
};

const buildSlotPlaceholder = (
  placeholder: EquipmentPlaceholder,
  displayLabel: string,
  // Kept blower EE spec capacity (BTU/hr). Written into maxHeatOutput as a
  // string so buildAirHandlerProductFromBlower maps it to heatingBTU through
  // the same parseAttributeInt path as a real product.
  heatingCapacityBtu?: number | null
): FullProductData => ({
  productId: KEPT_EQUIPMENT_PRODUCT_ID,
  manufacturer: '',
  modelNumber: '',
  name: equipmentPlaceholderName(placeholder, displayLabel),
  description: equipmentPlaceholderDescription(placeholder, displayLabel),
  placeholderNote: equipmentPlaceholderNoteText(placeholder),
  price: 0,
  isOnSale: false,
  weight: 0,
  productUrl: null,
  photo: null,
  fulfillableStock: 0,
  series: null,
  seriesDescription: null,
  seriesCode: null,
  height: null,
  width: null,
  depth: null,
  tonnage: null,
  stagesOfCool: null,
  stagesOfHeat: null,
  compressorType: null,
  soundRating: null,
  refrigerant: null,
  mobileHomeCompat: null,
  afue: null,
  blowerType: null,
  maxHeatOutput: heatingCapacityBtu != null ? String(heatingCapacityBtu) : null,
  cabinetSize: null,
  orientation: null,
  productType: displayLabel,
  voltage: null,
  amperage: null,
  warranties: []
});

const buildMissingSlotPlaceholder = (
  slot: MissingSlot,
  requiredLabel: string | undefined,
  keptLabel: string | undefined,
  keptHeatingCapacityBtu?: number | null
): FullProductData =>
  requiredLabel
    ? buildSlotPlaceholder(
        { role: 'required', label: requiredLabel },
        requiredLabel
      )
    : buildSlotPlaceholder(
        { role: 'kept' },
        keptLabel || KEPT_SLOT_DEFAULT_LABELS[slot],
        keptHeatingCapacityBtu
      );

// The zone-level snapshot shape (mirrors the code-only sizingThresholds
// addition to common's SystemOptionZoneSchema).
export type ZoneSizingThresholds = {
  cooling: { lowerPercent: number; upperPercent: number };
  heating: { lowerPercent: number; upperPercent: number };
};

// Resolves the per-side thresholds for one combination from the admin
// sizingBands setting. Cooling side picks the hpCooling band when the raw
// systemTypeLabel names a heat pump system — the rule the API's coverage
// enrichment applies (isHeatPumpSystemTypeLabel) — else acCooling; heating
// side is always the heating band. null bands (older API deploy / invalid
// record) resolve to null so callers attach nothing.
export function resolveZoneSizingThresholds(
  sizingBands: SizingBandsSettingType | null,
  systemTypeLabel: string
): ZoneSizingThresholds | null {
  if (!sizingBands) return null;
  const coolingType = isHeatPumpSystemTypeLabel(systemTypeLabel)
    ? 'hpCooling'
    : 'acCooling';
  // parseSizingBandsSetting guarantees exactly one row per type.
  const bandFor = (equipmentType: 'acCooling' | 'hpCooling' | 'heating') => {
    const row = sizingBands.find(
      (band) => band.equipmentType === equipmentType
    ) as SizingBandsSettingType[number];
    return { lowerPercent: row.lowerPercent, upperPercent: row.upperPercent };
  };
  return { cooling: bandFor(coolingType), heating: bandFor('heating') };
}

type SelectedSystemCondenser =
  SelectedSystemType['configuration']['condensers'][number];

export type CompatibilityCondenserBuild = {
  condenser: SelectedSystemCondenser;
  pair: CompatibilityPairRef;
  name: string;
  configurationUniqueId: string;
  configurationLabel: string;
  price: number;
  inStock: boolean;
  onSale: boolean;
  manufacturers: string[];
  seer: number;
  coolingBtu: number;
  amperage: number;
  keptPairing: boolean;
  quotableAccessories: SelectedSystemType['quotableAccessories'];
  ductedCoverage: DuctedCoverageType | null;
  operatingCost: DuctedOperatingCost | null;
  binCoverage: DuctedBinCoverage | null;
};

export type AssembleCompatibilitySelectedSystemParams = {
  quotableSystemUniqueId: string;
  pairs: CompatibilityCondenserBuild[];
  confidence?: ConfidenceScoreResultType | null;
  designConfigurationUniqueId?: string | null;
};

export function buildCompatibilityCondenser(
  params: CompatibilityPairParams
): CompatibilityCondenserBuild {
  const {
    configurationUniqueId,
    configurationLabel,
    systemTypeLabel,
    combinationPerformance,
    replacingProductIds,
    keptOutdoorLabel,
    keptIndoorLabel,
    keptBlowerLabel,
    keptBlowerHeatingCapacity,
    requiredOutdoorLabel,
    requiredIndoorLabel,
    requiredBlowerLabel,
    packagedUnitLabel,
    keptPairing,
    displayName,
    ductedCoverage,
    operatingCost,
    binCoverage,
    sizingBands,
    designPair
  } = params;

  // Use placeholders for open slots (null product = homeowner keeping their
  // existing unit, or a component they still have to source). Placeholder
  // productId 0 will never appear in replacingProductIds, so aggregates
  // (price/stock/manufacturers) are unaffected.
  const outdoorProduct =
    params.outdoorProduct ??
    buildMissingSlotPlaceholder(
      'outdoor',
      requiredOutdoorLabel,
      keptOutdoorLabel
    );
  // A packaged unit's indoor slot is the unit's own coil and blower: the
  // same sentinel productId (nothing to price or stock), but the section of
  // a purchased unit rather than kept or missing equipment.
  const packagedSection =
    params.indoorProduct === null && packagedUnitLabel !== undefined;
  const indoorProduct =
    params.indoorProduct ??
    (packagedUnitLabel !== undefined
      ? buildSlotPlaceholder({ role: 'packaged' }, packagedUnitLabel)
      : buildMissingSlotPlaceholder(
          'indoor',
          requiredIndoorLabel,
          keptIndoorLabel
        ));
  // The blower slot only becomes a placeholder when the caller says a kept
  // blower position exists (keptBlowerLabel set) or the system type names a
  // blower the homeowner still needs (requiredBlowerLabel set); a null slot
  // on a 2-part system type stays null — no phantom row.
  const blowerProduct =
    params.blowerProduct ??
    (requiredBlowerLabel || keptBlowerLabel
      ? buildMissingSlotPlaceholder(
          'blower',
          requiredBlowerLabel,
          keptBlowerLabel,
          keptBlowerHeatingCapacity
        )
      : null);

  // Replacing-only aggregates: price, inStock, onSale, manufacturers all
  // ignore kept equipment so the manufactured system represents what the
  // homeowner is actually buying.
  const replacingProducts = [outdoorProduct, indoorProduct, blowerProduct]
    .filter((p): p is FullProductData => p != null)
    .filter((p) => replacingProductIds.has(p.productId));

  const condenserUniqueId = nanoid();
  const zoneUniqueId = nanoid();

  // Build the blower row once so the indoor row's heating gate can key off
  // the blower's OWN heat output (parsed maxHeatOutput / kept EE spec), not
  // its mere presence — heat-less blowers (Modular Blowers) must not
  // suppress the combination heating on the indoor row.
  const blowerRow = blowerProduct
    ? buildAirHandlerProductFromBlower(blowerProduct)
    : null;

  const condenser: SelectedSystemCondenser = {
    uniqueId: condenserUniqueId,
    name: designPair?.condenserName ?? 'Outdoor Unit',
    type: 'ducted',
    selectedProduct: buildCondenserProduct(
      outdoorProduct,
      combinationPerformance
    ),
    zones: [
      {
        uniqueId: zoneUniqueId,
        name: blowerProduct ? 'Indoor Units' : 'Indoor Unit',
        type: 'ducted',
        zoneItems: [],
        selectedProduct: buildAirHandlerProductFromIndoor(
          indoorProduct,
          combinationPerformance,
          blowerRow != null && blowerRow.heatingBTU > 0,
          packagedSection
        ),
        ...(blowerRow ? { blowerProduct: blowerRow } : {})
      }
    ],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    noAccessoriesNeeded: true,
    heightOffGround: undefined
  };

  // Attached only when the sizingBands setting resolved — absence means
  // alpine-client falls back to its legacy hardcoded label thresholds.
  const zoneSizingThresholds = resolveZoneSizingThresholds(
    sizingBands ?? null,
    systemTypeLabel
  );
  if (zoneSizingThresholds) {
    condenser.zones[0].sizingThresholds = zoneSizingThresholds;
  }

  return {
    condenser,
    pair: {
      existingSystemUniqueId: params.existingSystemUniqueId ?? null,
      condenserUniqueId,
      configurationUniqueId,
      compatibilityCombinationId: params.compatibilityCombinationId ?? null,
      cardId: params.cardId ?? null,
      designCondenserUniqueId: designPair?.condenserUniqueId ?? null,
      designZoneUniqueId: designPair?.zoneUniqueId ?? null
    },
    name: displayName ?? systemTypeLabel,
    configurationUniqueId,
    configurationLabel,
    price: replacingProducts.reduce((sum, p) => sum + (p.price ?? 0), 0),
    inStock:
      replacingProducts.length > 0 &&
      replacingProducts.every((p) => p.fulfillableStock > 0),
    onSale: replacingProducts.some((p) => p.isOnSale),
    manufacturers: Array.from(
      new Set(replacingProducts.map((p) => p.manufacturer))
    ),
    seer: combinationPerformance.seer2 ?? 0,
    coolingBtu: combinationPerformance.coolingCapacity ?? 0,
    amperage: parseAttributeNumber(outdoorProduct.amperage),
    keptPairing: keptPairing === true,
    quotableAccessories: (params.accessories ?? []).map((acc) => ({
      productId: acc.productId,
      name: acc.name,
      description: acc.description ?? '',
      price: acc.price,
      // Cast required until @alpinehomeair/common is republished with the new
      // AccessoryTypeSchema values (txv, conversion_txv, communicating,
      // rds_board, rds_sensor, rds_board_and_sensor, lp_conversion_kit,
      // heat_kit).
      type: acc.accessoryTypeCode as any,
      productUrl: acc.productUrl ?? '',
      photo: acc.photo ?? '',
      availableStock: acc.fulfillableStock,
      quantity: 1
    })),
    ductedCoverage: ductedCoverage ?? null,
    operatingCost: operatingCost ?? null,
    binCoverage: binCoverage ?? null
  };
}

function uniqueInOrder(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function compatibilityBundleName(
  pairs: Array<Pick<CompatibilityCondenserBuild, 'name'>>
): string {
  return uniqueInOrder(pairs.map((pair) => pair.name)).join(' + ');
}

export function assembleCompatibilitySelectedSystem({
  quotableSystemUniqueId,
  pairs,
  confidence,
  designConfigurationUniqueId
}: AssembleCompatibilitySelectedSystemParams): SelectedSystemType {
  const [first] = pairs;
  const seers = pairs.map((pair) => pair.seer);
  const averageSeer =
    seers.reduce((sum, seer) => sum + seer, 0) / Math.max(seers.length, 1);
  const ductedCoverage = combineDuctedCoverage(
    pairs.map((pair) => pair.ductedCoverage)
  );
  const operatingCost = combineDuctedOperatingCost(
    pairs.map((pair) => pair.operatingCost)
  );
  const keptPairing = pairs.some((pair) => pair.keptPairing);

  const selectedSystem: SelectedSystemType = {
    id: `compat-${nanoid(5)}`,
    uniqueId: quotableSystemUniqueId,
    name: compatibilityBundleName(pairs),
    price: pairs.reduce((sum, pair) => sum + pair.price, 0),
    numberOfCondensers: pairs.length,
    numberOfZones: pairs.length,
    btu: pairs.reduce((sum, pair) => sum + pair.coolingBtu, 0),
    configuration: {
      uniqueId: first.configurationUniqueId,
      name: first.configurationLabel,
      condensers: pairs.map((pair) => pair.condenser)
    },
    // Option (a): the ducted percentages ride the non-nullable scores with
    // `?? 0` so existing numeric consumers (alpine-client v2Data reads)
    // self-fix; the "—" (null) case rides ONLY ductedCoverage below.
    heatingScore: ductedCoverage?.heatingCoveragePercent ?? 0,
    coolingScore: ductedCoverage?.coolingCoveragePercent ?? 0,
    // Stays [] for ducted CCB systems: the per-condenser hours-weighted
    // fields don't apply to the design-load metric.
    condenserCoverage: [],
    inStock: pairs.every((pair) => pair.inStock),
    restockDate: null,
    totalAnnualCost: operatingCost?.annualTotalCost ?? null,
    averageSeer,
    minSeer2: Math.min(...seers),
    maxSeer2: Math.max(...seers),
    averageAestheticsRank: 0,
    installationDifficultyScore: 0,
    manufacturers: uniqueInOrder(pairs.flatMap((pair) => pair.manufacturers)),
    tags: keptPairing
      ? [COMPATIBILITY_MATCH_TAG, KEPT_PAIRING_TAG]
      : [COMPATIBILITY_MATCH_TAG],
    onSale: pairs.some((pair) => pair.onSale),
    isMatchingAirHandlers: false,
    score: null,
    amperage: pairs.reduce((sum, pair) => sum + pair.amperage, 0),
    quoteStatus: 'unsent',
    selectedAccessories: [],
    quotableAccessories: pairs.flatMap((pair) => pair.quotableAccessories),
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    noAccessoriesNeeded: true
  };

  // Attached only when the server provided it — the field's presence is the
  // "this is a ducted CCB system" signal for the chip/subtitle surfaces.
  if (ductedCoverage) {
    selectedSystem.ductedCoverage = ductedCoverage;
  }

  // The cost breakdown rides along with the total so the comparison surfaces
  // can say which side of the estimate came from equipment the customer is
  // keeping.
  if (operatingCost) {
    selectedSystem.ductedOperatingCost = operatingCost;
  }

  if (pairs.length === 1 && first.binCoverage) {
    selectedSystem.ductedBinCoverage = first.binCoverage;
  }

  if (pairs.length > 1) {
    pairs.forEach(({ condenser, ductedCoverage: pairCoverage }) => {
      if (!pairCoverage) return;
      condenser.zones.forEach((zone) =>
        stampZoneDuctedCoverage(zone, pairCoverage)
      );
    });
  }

  if (confidence) {
    selectedSystem.confidence = confidence;
  }

  // Links the manufactured system back to the existing system whose placed
  // equipment positions drive the quote's synthetic ducted layout (read back
  // via readExistingSystemUniqueId).
  if (first.pair.existingSystemUniqueId) {
    selectedSystem.existingSystemUniqueId = first.pair.existingSystemUniqueId;
  }

  if (pairs.length === 1 && first.pair.compatibilityCombinationId != null) {
    selectedSystem.compatibilityCombinationId =
      first.pair.compatibilityCombinationId;
  }

  if (pairs.length > 1 || designConfigurationUniqueId) {
    stampCompatibilityBundleFields(selectedSystem, {
      designConfigurationUniqueId: designConfigurationUniqueId ?? null,
      compatibilityPairs: pairs.map((pair) => pair.pair)
    });
  }

  return selectedSystem;
}

export function buildSelectedSystemFromCompatibility(
  params: BuildSelectedSystemFromCompatibilityParams
): SelectedSystemType {
  return assembleCompatibilitySelectedSystem({
    quotableSystemUniqueId: params.quotableSystemUniqueId,
    pairs: [buildCompatibilityCondenser(params)],
    confidence: params.confidence,
    designConfigurationUniqueId:
      params.designPair?.designConfigurationUniqueId ?? null
  });
}
